import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import cancelOrder from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/CancelOrder";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import translate from "@insite/client-framework/Translate";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import { ShareEntityButtonStyles } from "@insite/content-library/Components/ShareEntityButton";
import { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { HiddenProps } from "@insite/mobius/Hidden";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const order = getOrderState(state, state.pages.orderDetails.orderNumber).value;
    const settingsCollection = getSettingsCollection(state);
    return {
        order,
        rmaLink: getPageLinkByPageType(state, "RequestRmaPage"),
        canReorderItems: settingsCollection.orderSettings.canReorderItems,
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        isReordering: state.pages.orderDetails.isReordering,
        isUpdatingCart: state.context.isUpdatingCart,
        isVmiOrderDetailsPage: state.pages.orderDetails.isVmiOrderDetailsPage,
    };
};

const mapDispatchToProps = {
    cancelOrder,
    reorder,
};

interface OwnProps {
    orderNumber: string;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface OrderDetailsButtonSetStyles {
    buttonsWrapper?: InjectableCss;
    buttonHidden?: HiddenProps;
    buttonGridContainer?: GridContainerProps;
    tabletButtonHidden?: HiddenProps;
    tabletMenuHidden?: HiddenProps;
    menuHidden?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    shareEntityButtonStyles?: ShareEntityButtonStyles;
    cancelButton?: ButtonPresentationProps;
    cancelClickable?: ClickablePresentationProps;
    printButton?: ButtonPresentationProps;
    printClickable?: ClickablePresentationProps;
    reorderButton?: ButtonPresentationProps;
    spinner?: LoadingSpinnerProps;
    reorderSpinner?: LoadingSpinnerProps;
    reorderClickable?: ClickablePresentationProps;
    reorderClickableSpinner?: LoadingSpinnerProps;
    rmaButton?: ButtonPresentationProps;
    rmaClickable?: ClickablePresentationProps;
    buttonWrapper?: InjectableCss;
    twoButtonModalStyles?: TwoButtonModalStyles;
    restrictedButtonModalStyles?: ModalPresentationProps;
    btnWrapper?: InjectableCss;
    messageWrappper?: InjectableCss;
}

export const orderDetailsButtonSetStyles: OrderDetailsButtonSetStyles = {
    buttonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            clear: both;
            button[data-test-selector^="popoverOverflowTrigger"] {
                width: 40px;
                background-color: transparent;
                border-color: transparent;
                display: flex;
                align-items: flex-start;
                justify-content: flex-end;
            }
        `,
    },
    shareEntityButtonStyles: {
        button: {
            variant: "secondary",
        },
    },
    buttonHidden: {
        below: "lg",
        css: css`
            width: 100%;
            display: flex;
            justify-content: flex-end;
        `,
    },
    tabletButtonHidden: {
        below: "md",
        above: "md",
    },
    tabletMenuHidden: {
        below: "md",
        above: "md",
    },
    menuHidden: {
        above: "sm",
        css: css`
            width: 100%;
            display: flex;
            justify-content: flex-end;
        `,
    },
    buttonWrapper: {
        css: css`
            margin-left: 20px;
        `,
    },
    cancelButton: {
        buttonType: "outline",
        variant: "secondary",
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
    },
    reorderButton: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    rmaButton: {
        buttonType: "solid",
        variant: "tertiary",
    },
    spinner: {
        size: 22,
        css: css`
            margin: 8px 50px 0 34px;
        `,
    },
    reorderSpinner:{
        size: 22,
        css: css`
            margin: auto;
        `
    },
    reorderClickableSpinner: {
        size: 22,
        css: css`
            margin: 10px 0 5px 15px;
        `,
    },
    twoButtonModalStyles: {
        submitButton: {
            color: "primary",
        },
    },
    reorderClickable:{
        css: css`
            text-align: left;
        `
    },
    restrictedButtonModalStyles:{
             cssOverrides: {
                modalTitle: css`
                    border-bottom: none;
                    padding: 0;
                    position: relative;
                    #CloseModalButton{
                        position: absolute;
                        top: 5px;
                        right: 15px;
                    }
                `,
                modalContent:css`
                    > div:first-of-type{
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    padding: 0;
                                `,
                                css`
                                     padding: 0;
                                `,
                                css``,
                                css``,
                                css``,
                        ])}
                    }
                    span{
                        font-size: 19px;
                        display: block;
                    }
                `
            },
        },
        btnWrapper:{
            css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            margin-top: 30px;
            button{
                &:first-child{
                    margin-right: 20px;
                }
            }
        `
    },
    messageWrappper:{
        css: css`
            display: flex;
            justify-content: flex-start;
            align-items: center;
            min-height: 111px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        min-height: 115px;
                    `,
                    css`
                       min-height: 115px;
                    `,
                    css``,
                    css``,
                    css``,
            ])}
        `
    }
};

/**
 * @deprecated Use orderDetailsButtonSetStyles instead.
 */
export const emailButtonStyles = orderDetailsButtonSetStyles;
const styles = orderDetailsButtonSetStyles;

interface RestrictedItem {
  productNumber: string;
  title: string;
}

const OrderDetailsKebabMenu: React.FC<Props> = ({
    orderNumber,
    reorder,
    showAddToCartConfirmationDialog,
    isReordering,
    isUpdatingCart,
    isVmiOrderDetailsPage,
}) => {
    const toasterContext = React.useContext(ToasterContext);
    const [showRestrictedItemsModal, setShowRestrictedItemsModal] = useState(false); 
    const [restrictedItems, setRestrictedItems] = useState<RestrictedItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);

     const restrictedItemsMessage = siteMessage(
        "OrderHistory_RestrictedItemsCannotBeReordered",
      restrictedItems
        .map(item => `${item.productNumber} - ${item.title}`)
        .join("\n")
    );
        

    /* if (!order) {
        return null;
    } */

    /* const onClickPrint = () => {
        if (!order) {
            return;
        }
        openPrintDialog();
    }; */
     
    const onReorderSuccess = (orderNumber: string) => {
        if (!showAddToCartConfirmationDialog) {
            return;
        }
        toasterContext.addToast({
            body: (
                <OrderDetailPageTypeLink
                    title={orderNumber}
                    orderNumber={orderNumber}
                    isVmiOrderDetailsPage={isVmiOrderDetailsPage}
                />
            ),
            messageType: "success",
            timeoutLength: 6000,
        });
    };
    const onReorderError = (message: string) => {
        toasterContext.addToast({
            body: message,
            messageType: "danger",
            timeoutLength: 6000,
        });
    };

    const handleReorder = () => {
        setIsLoading(true);
        (reorder as any)({
            orderNumber,
            skipRestrictedItemsCheck: true,
            onSuccess: () => {
                setIsLoading(false);
                setShowRestrictedItemsModal(false);
                onReorderSuccess(orderNumber);
            },
            onError: (error: string) => {
                toasterContext.addToast({ body: error, messageType: "danger", timeoutLength: 6000 });
            },
        });

    }

    const onClickReorder = () => {
        reorder({
            orderNumber,
            onSuccess: () => onReorderSuccess(orderNumber),
            onError: (error: string) => {
                let parsed: any = {};
                try {
                    parsed = JSON.parse(error);
                } catch {
                    // Not a JSON string, show plain error message
                    toasterContext.addToast({
                        body: error,
                        messageType: "danger",
                        timeoutLength: 6000,
                    });
                    return;
                }

                if (parsed.code === "RESTRICTED_ITEMS") {
                    setRestrictedItems(parsed.items || []);
                    setShowRestrictedItemsModal(true);
                } else {
                    toasterContext.addToast({
                        body: parsed.message || error,
                        messageType: "danger",
                        timeoutLength: 6000,
                    });
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.();
                }
            },
        });
    };


    return (
        <StyledWrapper {...styles.buttonsWrapper} className="kabab-menu">
            <OverflowMenu hasChildPortal position="end" {...styles.overflowMenu}>
                {!isReordering && (
                    <Clickable {...styles.reorderClickable} onClick={onClickReorder} disabled={isUpdatingCart}>
                        {translate("Reorder")}
                    </Clickable>
                )}
                {isReordering && <LoadingSpinner {...styles.reorderClickableSpinner} />}
            </OverflowMenu>
            <Modal
                isOpen={showRestrictedItemsModal}
                handleClose={() => {
                    if (!isLoading) {
                        setShowRestrictedItemsModal(false);
                    }
                }}
                sizeVariant="medium"
                {...styles.restrictedButtonModalStyles}
            >
                <StyledWrapper {...styles.messageWrappper}>
                    {isLoading && (
                        <LoadingSpinner {...styles.reorderSpinner} />
                    )}

                    {!isLoading && (
                        <Typography>{restrictedItemsMessage}</Typography>
                    )}
                </StyledWrapper>  
                <StyledWrapper {...styles.btnWrapper}>
                    <Button
                        {...styles.cancelButton}
                        onClick={() => setShowRestrictedItemsModal(false)}
                        disabled={isLoading}
                    >
                       {translate("Cancel")}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleReorder}
                        disabled={isLoading}
                    >
                        {translate("Proceed")}
                    </Button>
                </StyledWrapper>      
            </Modal>
        </StyledWrapper>
        
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(OrderDetailsKebabMenu));